/**
 * Which Decision produced a ranking, so its call can be drawn where the rank
 * is drawn and not three clicks away on the decision's own page (owner,
 * 2026-10-30: "a standing is never drawn alone"; canon 02B L1, the flag wears
 * where the decision is being made).
 *
 * Three kinds of rank, three records (canon 27 §4; the bridge of 568 and 788):
 *   - a rank of record: the Decision `authorize_por` wrote (source
 *     `plan_of_record_authorized`), named by the plan-of-record snapshot the
 *     authorization described, or failing that its plan version;
 *   - a priority-list rank: the Decision the bridge wrote when the list was
 *     approved in Catchball (`priority_list_approved`), ratified
 *     (`priority_set_ratified`) or published (`priority_set_published`;
 *     migration 1007), the newest act first. The decision row does not name
 *     the list; its governance event does, so the caller passes that map
 *     (rankingDecisionService.ts reads it);
 *   - a session rank: the Decision written when the session's result was
 *     published (`session_published`, `session_result_published`,
 *     `official_result_set`), found by session.
 * A proposed list has no decision: nobody has agreed to it yet. That is said
 * in words, never guessed around (canon 02B L1: "no basis", never a smoothed
 * guess). Pure: no I/O, unit-tested in rankingDecision.test.ts.
 */
import type { Decision } from '../../types/prioritization/decisions';
import type { ItemRanking } from '../itemRankings';

export const PLAN_RANK_SOURCES: readonly string[] = ['plan_of_record_authorized'];
export const LIST_RANK_SOURCES: readonly string[] = ['priority_list_approved', 'priority_set_ratified', 'priority_set_published'];
export const SESSION_RANK_SOURCES: readonly string[] = ['session_published', 'session_result_published', 'official_result_set'];

/** Why a ranking has no decision to read. */
export type RankingDecisionGap =
  /** The list is proposed; nobody has ratified it, so there is no decision yet. */
  | 'proposed_list'
  /** The record holds no decision for this ranking. */
  | 'not_on_record';

export interface RankingDecision {
  decision: Decision | null;
  gap: RankingDecisionGap | null;
}

function newestFirst(a: Decision, b: Decision): number {
  return (b.decidedAt ?? '').localeCompare(a.decidedAt ?? '');
}

/** The live decision when there is one, else the newest; the record's own order. */
function prefer(candidates: readonly Decision[]): Decision | null {
  const live = candidates.filter((d) => d.status === 'active');
  return [...(live.length ? live : candidates)].sort(newestFirst)[0] ?? null;
}

function names(decision: Decision, kind: string, id: string): boolean {
  return (decision.relatedObjects ?? []).some((o) => o.kind === kind && o.id === id);
}

export function decisionForRanking(
  ranking: ItemRanking,
  decisions: readonly Decision[],
  listDecisionIdBySet: ReadonlyMap<string, string> = new Map(),
): RankingDecision {
  switch (ranking.standing) {
    case 'official':
    case 'starts_later':
    case 'replaced': {
      const authorizations = decisions.filter((d) => PLAN_RANK_SOURCES.includes(d.source ?? ''));
      const snapshot = ranking.planOfRecordSnapshotId ?? null;
      const version = ranking.planVersionId ?? null;
      const bySnapshot = snapshot ? authorizations.filter((d) => names(d, 'plan_of_record_snapshot', snapshot)) : [];
      // A ranking that names its snapshot is read by that snapshot alone: a later
      // authorization of the same plan version is another snapshot's call (seen on
      // production 2026-11-02: a replaced Jul 9 rank drew the Oct 30 call). The
      // version fallback is for rankings recorded before snapshots were kept.
      const byVersion = !snapshot && version ? authorizations.filter((d) => names(d, 'plan_version', version)) : [];
      const decision = prefer(bySnapshot.length ? bySnapshot : byVersion);
      return { decision, gap: decision ? null : 'not_on_record' };
    }
    case 'agreed': {
      const id = ranking.prioritySetId ? listDecisionIdBySet.get(ranking.prioritySetId) : undefined;
      const decision = id ? decisions.find((d) => d.id === id) ?? null : null;
      return { decision, gap: decision ? null : 'not_on_record' };
    }
    case 'proposed':
      return { decision: null, gap: 'proposed_list' };
    case 'session': {
      const decision = ranking.sessionId
        ? prefer(decisions.filter((d) => d.sessionId === ranking.sessionId && SESSION_RANK_SOURCES.includes(d.source ?? '')))
        : null;
      return { decision, gap: decision ? null : 'not_on_record' };
    }
    default:
      return { decision: null, gap: 'not_on_record' };
  }
}

/** The honest line drawn where the call would be, when there is none to read. */
export function rankingDecisionGapText(ranking: Pick<ItemRanking, 'standing'>, gap: RankingDecisionGap): string {
  if (gap === 'proposed_list') return 'Proposed, not yet agreed: there is no decision to read until the list is ratified.';
  if (ranking.standing === 'session') return 'No decision is on the record for this session rank: the result was never published as a decision.';
  // Since migration 1007 ratifying and publishing a list write a Decision; a
  // list agreed before it keeps its events and none. Said plainly, not guessed at.
  if (ranking.standing === 'agreed') return 'No decision is on the record for this list.';
  return 'No decision is on the record for this ranking.';
}

// ---------------------------------------------------------------------------
// The call behind a list, and behind a cycle report (owner, 2026-10-30, the
// readout's item 2: the standing of the whole list is drawn with its call once;
// a row on it has no act of its own, only its exceptions).
// ---------------------------------------------------------------------------

/** The acts that write a cycle report: accepting the proposal that produced it. */
export const REPORT_RATIFICATION_SOURCES: readonly string[] = ['proposal_accepted', 'proposal_accepted_provisional'];

/** A list that has been agreed: ratified or published. Anything else is still being argued. */
export function isAgreedListState(state: string | null | undefined): boolean {
  return state === 'ratified' || state === 'published';
}

/**
 * The Decision behind a priority list's standing: the one its ratification or
 * publication wrote (migration 1007), found through its governance event. A
 * list still proposed has none, and a list agreed before 1007 keeps none.
 */
export function decisionForList(
  list: { id: string; state: string },
  decisions: readonly Decision[],
  listDecisionIdBySet: ReadonlyMap<string, string>,
): RankingDecision {
  if (!isAgreedListState(list.state)) return { decision: null, gap: 'proposed_list' };
  const id = listDecisionIdBySet.get(list.id);
  const decision = id ? decisions.find((d) => d.id === id) ?? null : null;
  return { decision, gap: decision ? null : 'not_on_record' };
}

export function listDecisionGapText(state: string | null | undefined): string {
  if (!isAgreedListState(state)) return 'Proposed, not yet agreed: there is no decision to read until the list is ratified.';
  return 'No decision is on the record for this list.';
}

function sameInstant(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (Number.isNaN(ta) || Number.isNaN(tb)) return false;
  // One transaction stamps both rows with one `now()`; a second of slack only
  // covers the microseconds a JavaScript Date drops.
  return Math.abs(ta - tb) <= 1000;
}

export interface CycleReportDecision extends RankingDecision {
  /**
   * Where the call was read from: the decision publishing the report wrote
   * (migration 1008), the list's own decision, or the acceptance that wrote the
   * report.
   */
  basis: 'report' | 'list' | 'acceptance' | null;
}

/** The source publish_cycle_report's event crosses the bridge under (migration 1008). */
export const REPORT_PUBLICATION_SOURCES: readonly string[] = ['cycle_report_published'];

/**
 * The Decision behind a cycle report's standing. Accepting a Catchball proposal
 * writes the report in the same transaction (`catchball_accept` →
 * `write_cycle_report`), so the acceptance decision carries the report's cycle
 * and the report's ratification instant: that is the exact link, not a guess.
 * When the report's list has a decision of its own (ratified or published
 * after migration 1007), that one is the later act and wins.
 */
export function decisionForCycleReport(
  report: { cycleId: string; ratifiedAt?: string | null; publishedAt?: string | null; prioritySetId?: string | null },
  decisions: readonly Decision[],
  listDecisionIdBySet: ReadonlyMap<string, string>,
): CycleReportDecision {
  // A published report's own decision: publish_cycle_report stamps the report
  // and its event with one now(), so the instant finds it (migration 1008).
  const own = report.publishedAt
    ? prefer(
        decisions.filter(
          (d) => REPORT_PUBLICATION_SOURCES.includes(d.source ?? '') && d.cycleId === report.cycleId && sameInstant(d.decidedAt, report.publishedAt),
        ),
      )
    : null;
  if (own) return { decision: own, gap: null, basis: 'report' };
  const listId = report.prioritySetId ? listDecisionIdBySet.get(report.prioritySetId) : undefined;
  const fromList = listId ? decisions.find((d) => d.id === listId) ?? null : null;
  if (fromList) return { decision: fromList, gap: null, basis: 'list' };
  const acceptance = prefer(
    decisions.filter(
      (d) => REPORT_RATIFICATION_SOURCES.includes(d.source ?? '') && d.cycleId === report.cycleId && sameInstant(d.decidedAt, report.ratifiedAt),
    ),
  );
  if (acceptance) return { decision: acceptance, gap: null, basis: 'acceptance' };
  return { decision: null, gap: 'not_on_record', basis: null };
}

export const CYCLE_REPORT_DECISION_GAP_TEXT =
  'No decision is on the record for this report: the acceptance that produced it was not written as a decision.';

/**
 * Which of an item's rankings the "Why is this #N?" panel is about. The rank
 * the person clicked wins, on the surface they clicked it when that is known;
 * otherwise the most authoritative ranking, which is how the list is ordered.
 */
export function pickRankingForTrail(
  rankings: readonly ItemRanking[],
  rankLabel?: number | string | null,
  contextLabel?: string | null,
): ItemRanking | null {
  if (!rankings.length) return null;
  const n = rankLabel == null ? NaN : Number(String(rankLabel).replace(/^T/i, ''));
  if (!Number.isNaN(n)) {
    const sameRank = rankings.filter((r) => r.rank === n);
    if (sameRank.length) {
      const onSurface = contextLabel ? sameRank.find((r) => r.sourceName === contextLabel) : undefined;
      return onSurface ?? sameRank[0];
    }
  }
  return rankings[0];
}

// ── The call behind an incoming list, at the accept moment ──────────────────

export type IncomingListGap = 'no_session' | 'not_on_record';

export interface IncomingListDecision extends RankingDecision {
  /**
   * Where the call was read from: the publication of the session the list
   * came from, or the Decision the ratification or publication of the list
   * that session sent wrote (migration 1008 writes the session on it).
   */
  basis: 'session' | 'list' | null;
  gap: IncomingListGap | null;
}

/**
 * The decision behind the order a proposal carries, before anyone accepts it
 * (readout item 3: the accept moment). A packet names the session that ranked
 * it; the record holds either that session's own publication or, since 1008,
 * the Decision of the list the session sent up with the session on it. The
 * newest live one speaks; where the record holds neither, the gap is said.
 */
export function decisionForIncomingList(
  sessionId: string | null | undefined,
  decisions: readonly Decision[],
): IncomingListDecision {
  if (!sessionId) return { decision: null, gap: 'no_session', basis: null };
  const session = prefer(decisions.filter((d) => d.sessionId === sessionId && SESSION_RANK_SOURCES.includes(d.source ?? '')));
  if (session) return { decision: session, gap: null, basis: 'session' };
  const list = prefer(decisions.filter((d) => d.sessionId === sessionId && LIST_RANK_SOURCES.includes(d.source ?? '')));
  if (list) return { decision: list, gap: null, basis: 'list' };
  return { decision: null, gap: 'not_on_record', basis: null };
}

export function incomingListGapText(gap: IncomingListGap): string {
  switch (gap) {
    case 'no_session':
      return 'This proposal names no session, so the record holds no decision behind the order it carries.';
    case 'not_on_record':
      return 'The session this list came from wrote no decision on the record. Accepting writes the first decision about it.';
  }
}

export function incomingListBasisText(basis: IncomingListDecision['basis']): string {
  return basis === 'session'
    ? 'Read from the publication of the session this list came from.'
    : "Read from the decision the ratification or publication of the list this session sent wrote.";
}
